"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, Bot, CalendarCheck2, CircleCheck as CheckCircle2, Cpu, Layers, Radio, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
const benefitsData = [{
  id: "b1",
  title: "Zero-Friction Scheduling",
  description: "Direct GoHighLevel calendar synchronization converts AI search queries into verified consultation calls automatically.",
  metric: "100%",
  metricLabel: "Automated Handshake",
  icon: "CalendarCheck2"
}, {
  id: "b2",
  title: "Direct Pipeline Velocity",
  description: "Qualified prospects skip index clutter and enter your booking flow immediately upon AI engine citation.",
  metric: "< 2 min",
  metricLabel: "Query to Booking",
  icon: "Activity"
}, {
  id: "b3",
  title: "Protected Lead Capture",
  description: "Multi-layered fallback mechanisms ensure high-intent prospective calls never get lost in transition.",
  metric: "24/7",
  metricLabel: "System Uptime",
  icon: "ShieldCheck"
}, {
  id: "b4",
  title: "Attributed Revenue Stream",
  description: "Real-time analytics track exact booked appointments and revenue back to original generative citations.",
  metric: "Live",
  metricLabel: "Tracking Telemetry",
  icon: "TrendingUp"
}];
const ecosystemNodes = [{
  id: "ai_citations",
  nodeNumber: "01",
  title: "Generative AI Citations",
  subtitle: "ChatGPT, Perplexity & Claude",
  badge: "INPUT LAYER",
  status: "Active Scanning",
  detail: "AI search engines identify ClearLine entity references and generate authoritative answers recommending your service."
}, {
  id: "clearline_core",
  nodeNumber: "02",
  title: "ClearLine GEO Synapse",
  subtitle: "Structured Schema & Entity Validation",
  badge: "PROCESSING",
  status: "Schema Synchronized",
  detail: "Machine-readable structured schema feeds verified local service availability directly to LLM crawlers."
}, {
  id: "ghl_booking",
  nodeNumber: "03",
  title: "Instant Booking Sync",
  subtitle: "CRM Direct Routing",
  badge: "CONVERSION",
  status: "Instant Handshake",
  detail: "AI-referred prospects land on dedicated conversion endpoints with pre-populated scheduling parameters."
}, {
  id: "revenue_close",
  nodeNumber: "04",
  title: "Attributed Deal Close",
  subtitle: "Closed-Loop Telemetry",
  badge: "REVENUE",
  status: "Verified ROI",
  detail: "Real-time reporting logs completed consultations back to the original AI search model source."
}];
export default function BookingImpact() {
  const [activeNodeId, setActiveNodeId] = useState<string>("ghl_booking");
  const shouldReduceMotion = useReducedMotion();
  const activeNode = ecosystemNodes.find(n => n.id === activeNodeId) || ecosystemNodes[2];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="bookingimpact" data-nav="dark" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-y-2 border-border">
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[var(--accent-deep-red)] via-transparent to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-md border border-border bg-card text-card-foreground font-mono text-xs tracking-widest uppercase">
            <Radio className="size-3 text-[var(--accent-deep-red)] animate-pulse" />
            <span>{"SYSTEM // BOOKING_CONVERSION_ENGINE"}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-6">
            Turn AI Visibility Into Confirmed <span className="text-[var(--accent-deep-red)] font-serif italic">Calls</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When generative engines recommend your business, ClearLine ensures high-intent prospects move directly into your booking calendar without friction or third-party drop-off.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefitsData.map((benefit, i) => {
          return <motion.div key={benefit.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border-2 border-border bg-card/60 backdrop-blur-xs hover:border-[var(--accent-deep-red)]/60 transition-all duration-300 flex flex-col justify-between group">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-md bg-muted text-foreground border border-border group-hover:border-[var(--accent-deep-red)]/40 transition-colors">
                        {benefit.icon === "CalendarCheck2" && <CalendarCheck2 className="size-6 text-[var(--accent-deep-red)]" />}
                        {benefit.icon === "Activity" && <Activity className="size-6 text-[var(--accent-deep-red)]" />}
                        {benefit.icon === "ShieldCheck" && <ShieldCheck className="size-6 text-[var(--accent-deep-red)]" />}
                        {benefit.icon === "TrendingUp" && <TrendingUp className="size-6 text-[var(--accent-deep-red)]" />}
                      </div>
                      <Badge variant="outline" className="font-mono text-xs border-border bg-background">
                        {benefit.metric}
                      </Badge>
                    </div>

                    <div>
                      <CardTitle className="text-xl font-bold font-default text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors mb-2">
                        {benefit.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground leading-normal">
                        {benefit.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Separator className="mb-4 bg-border" />
                    <div className="flex items-center justify-between font-mono text-xs text-muted-foreground">
                      <span>Telemetry:</span>
                      <span className="text-foreground font-semibold">{benefit.metricLabel}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <Separator className="my-16 bg-border" />

        <div className="border-2 border-border bg-card/40 rounded-xl p-6 sm:p-8 lg:p-10 relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <span className="font-mono text-xs text-[var(--accent-deep-red)] tracking-widest uppercase block mb-1">
                {"// ECOSYSTEM_DIAGRAM"}
              </span>
              <h3 className="text-2xl font-bold font-default text-foreground">
                AI Query To Booking Pipeline
              </h3>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono text-xs bg-muted text-foreground">
                <Sparkles className="size-3 text-[var(--accent-deep-red)] mr-1" />
                Live Node Routing
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {ecosystemNodes.map((node, index) => {
            const isActive = node.id === activeNodeId;
            return <div key={node.id} data-index={index}>
                  <Button variant="ghost" onClick={() => setActiveNodeId(node.id)} className={`w-full h-auto p-4 text-left flex flex-col items-start justify-between rounded-lg border-2 transition-all ${isActive ? "border-[var(--accent-deep-red)] bg-muted/60 shadow-md" : "border-border bg-background/50 hover:border-border/80"}`}>
                    <div className="w-full flex items-center justify-between mb-3">
                      <span className="font-mono text-xs font-bold text-[var(--accent-deep-red)]">
                        NODE_{node.nodeNumber}
                      </span>
                      <Badge variant={isActive ? "default" : "outline"} className={`text-[10px] font-mono uppercase ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "border-border"}`}>
                        {node.badge}
                      </Badge>
                    </div>

                    <div className="w-full">
                      <p className="font-bold font-default text-sm text-foreground mb-1">
                        {node.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {node.subtitle}
                      </p>
                    </div>

                    <div className="w-full mt-4 pt-3 border-t border-border flex items-center justify-between font-mono text-[11px]">
                      <span className="text-muted-foreground">Status:</span>
                      <span className={isActive ? "text-[var(--accent-deep-red)] font-bold" : "text-foreground"}>
                        {node.status}
                      </span>
                    </div>
                  </Button>
                </div>;
          })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={activeNode.id} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : -10
          }} transition={{
            duration: 0.3
          }} className="p-6 rounded-lg border-2 border-border bg-background flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="space-y-2 max-w-2xl">
                <div className="flex items-center gap-2">
                  <Cpu className="size-4 text-[var(--accent-deep-red)]" />
                  <span className="font-mono text-xs text-[var(--accent-deep-red)] uppercase tracking-wide">
                    Active Synapse Detail // {activeNode.nodeNumber}
                  </span>
                </div>
                <h4 className="text-xl font-bold font-default text-foreground">
                  {activeNode.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {activeNode.detail}
                </p>
              </div>

              <div className="flex items-center gap-4 shrink-0 w-full lg:w-auto justify-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="font-mono text-xs border-border">
                        <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)] mr-1.5" />
                        Verified Endpoint
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>GoHighLevel calendar endpoint verified & operational.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <Button asChild size="sm" className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90">
                  <Link to="/ContactPage">
                    <span>Inspect Setup</span>
                    <ArrowRight className="size-3.5 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-16 p-8 lg:p-12 rounded-xl border-2 border-[var(--accent-deep-red)]/40 bg-card/80 text-center relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-6 relative z-10">
            <Badge variant="outline" className="border-[var(--accent-deep-red)] text-[var(--accent-deep-red)] font-mono text-xs bg-background">
              DIRECT BOOKING CONVERSION
            </Badge>

            <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
              Ready to Capture High-Intent AI Search Leads?
            </h3>

            <p className="text-sm sm:text-base text-muted-foreground">
              Schedule a strategy call or request a complimentary AI visibility audit to measure your current engine rankings.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90">
                <Link to="/PricingPlansPage">get started now</Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border">
                <Link to="/VisibilityCheckupPage">
                  <span>Free AI Visibility Checkup</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}